/*
 * The editor's implementation of the shell's data contract (ADR-008, phase 1).
 *
 * This is the only place that knows both sides. The shell never imports the
 * editor; the editor describes itself in the shell's terms here, and each
 * adoption phase moves one more region from `DEMO_HOST` fixtures to real state.
 */

import type { DocumentSummary, ShellHost } from '../../shell/host';
import type { EditorSession } from './types';
import { walkLayers } from '../core/model';

/**
 * `MIXED` / `RASTER` / `VECTOR`, read from the layers the document actually
 * has rather than from a document-level flag, because the model has no such
 * flag. A document with a solid background is vector by that reading — the
 * background *is* a vector layer — so a painted document reports `MIXED`. That
 * is the model's own answer, not a heuristic layered on top of it.
 */
function mediumOf(session: EditorSession): string | undefined {
  let raster = false;
  let vector = false;
  for (const layer of walkLayers(session.document.root)) {
    if (layer.kind === 'raster') raster = true;
    if (layer.kind === 'vector' || layer.kind === 'text') vector = true;
    if (raster && vector) return 'MIXED';
  }
  if (raster) return 'RASTER';
  if (vector) return 'VECTOR';
  return undefined;
}

function summarise(session: EditorSession): DocumentSummary {
  const { document } = session;
  const spec = `${String(document.width)} × ${String(document.height)}`;
  const medium = mediumOf(session);
  return {
    id: session.sessionId,
    name: document.name,
    spec,
    // Read from the colour block rather than written as a literal, so a future
    // depth or space appears here without this line being remembered.
    detail: `${spec} · ${document.color.space.toUpperCase()}/${String(document.color.depth)}`,
    ...(medium ? { medium } : {}),
    dirty: session.dirty,
  };
}

/**
 * Builds the host from the editor's real sessions.
 *
 * `menus` is deliberately empty: the editor's menu bar is inline JSX carrying
 * per-item enablement and command closures, and it is lent to the shell as a
 * node until phase 5 turns it into data. An empty record renders no fixture
 * menu, which is the honest state — not a menu that looks live and is not.
 */
export function createEditorHost(sessions: readonly EditorSession[], activeId: string): ShellHost {
  return {
    documents: sessions.map(summarise),
    activeDocumentId: activeId,
    menus: {},
  };
}
